import React, { useEffect, useMemo, useRef, useState } from 'react';
import NewsComments from './NewsComments';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookmark,
  faClock,
  faComment,
  faFloppyDisk,
  faThumbtack,
  faUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import {
  getTimeAgo,
  setAdminPinnedArticle,
  setNewsArticleHiddenState,
} from '../firebase/newsHelpers';

const clampText = (value) => String(value || '').trim();

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatBlueWireTime = (article) => {
  const millis = Number(article?.feedRunAtMs || 0) || toMillis(article?.feedRunAt);
  if (!millis) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .formatToParts(new Date(millis))
    .reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

  const hour = parts.hour || '';
  const minute = parts.minute && parts.minute !== '00' ? `:${parts.minute}` : '';
  const dayPeriod = String(parts.dayPeriod || '').toLowerCase();
  return `${hour}${minute}${dayPeriod}`;
};

export default function NewsCard({
  article,
  articleCollection = 'newsArticles',
  currentUser,
  isSaved,
  isPinned,
  isAdmin,
  onToggleSave,
  onTogglePin,
}) {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const commentsButtonRef = useRef(null);
  const commentsWrapRef = useRef(null);
  const hasImage = clampText(article.imageUrl) && !imageFailed;
  const blueWireTime = formatBlueWireTime(article);

  const previewText = useMemo(() => {
    const summary = clampText(article.summary);
    const description = clampText(article.description);
    return summary || description || article.title;
  }, [article.description, article.summary, article.title]);

  useEffect(() => {
    if (!commentsOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;

      if (
        commentsOpen &&
        commentsWrapRef.current &&
        commentsButtonRef.current &&
        !commentsWrapRef.current.contains(target) &&
        !commentsButtonRef.current.contains(target)
      ) {
        setCommentsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setCommentsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [commentsOpen]);

  const handleAdminPin = async () => {
    const currentlyPinned = article.adminPinned === true;
    let nextPriority = 1;

    if (!currentlyPinned) {
      const input = window.prompt('Admin pin priority (1 = top)', String(article.adminPinPriority || 1));
      if (input === null) return;
      nextPriority = Number(input || 1);
      if (!Number.isFinite(nextPriority) || nextPriority < 1) {
        alert('Enter a valid number.');
        return;
      }
    }

    try {
      setAdminBusy(true);
      await setAdminPinnedArticle(
        article.id,
        !currentlyPinned,
        nextPriority,
        articleCollection
      );
    } catch (error) {
      alert(error?.message || 'Could not update admin pin.');
    } finally {
      setAdminBusy(false);
    }
  };

  const handleAdminHide = async (shouldHide) => {
    try {
      setAdminBusy(true);
      await setNewsArticleHiddenState(article.id, shouldHide, articleCollection);
    } catch (error) {
      alert(error?.message || 'Could not update article visibility.');
    } finally {
      setAdminBusy(false);
    }
  };

  return (
    <article className={`news-card ${article.isHidden ? 'news-card-hidden' : ''}`}>
      <header className="news-card-header">
        <div className="news-card-heading">
          <div className="news-card-source-row">
            <span className="news-card-source">{article.source || 'Unknown Source'}</span>
            {article.isPaywalled ? (
              <span className="news-badge news-badge-paywall">Paywalled</span>
            ) : null}
            {blueWireTime ? (
              <span className="news-card-bluewire-time" title="Added to BlueWire">
                <FontAwesomeIcon icon={faClock} />
                {blueWireTime}
              </span>
            ) : null}
          </div>
          <h3 className="news-card-title">{article.title}</h3>
        </div>
        <div className="news-card-time">{getTimeAgo(article.publishedAt)}</div>
      </header>

      <div className="news-card-tags">
        {(article.tags || []).map((tag) => (
          <span key={tag} className="news-tag">
            {tag}
          </span>
        ))}
      </div>

      {hasImage ? (
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="news-card-image-link"
          aria-label={`Open article from ${article.source || 'source'}`}
        >
          <img
            src={article.imageUrl}
            alt=""
            className="news-card-image"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        </a>
      ) : null}

      <div className="news-card-body">
        <p className="news-card-text">{previewText}</p>
      </div>

      <div className="news-card-actions">
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="news-icon-button news-source-button"
          aria-label="Open source article"
          title="Open source"
        >
          <FontAwesomeIcon icon={faUpRightFromSquare} />
        </a>
        <button
          type="button"
          className={`news-icon-button ${isSaved ? 'active saved' : ''}`}
          onClick={() => onToggleSave(article)}
          aria-pressed={isSaved}
          aria-label={isSaved ? 'Unsave article' : 'Save article'}
          title={isSaved ? 'Unsave' : 'Save'}
        >
          <FontAwesomeIcon icon={isSaved ? faBookmark : faFloppyDisk} />
        </button>
        <button
          type="button"
          className={`news-icon-button ${isPinned ? 'active pinned' : ''}`}
          onClick={() => onTogglePin(article.id)}
          aria-pressed={isPinned}
          aria-label={isPinned ? 'Unpin article' : 'Pin article'}
          title={isPinned ? 'Unpin' : 'Pin'}
        >
          <FontAwesomeIcon icon={faThumbtack} />
        </button>
        <button
          ref={commentsButtonRef}
          type="button"
          className={`news-icon-button news-comments-toggle ${commentsOpen ? 'active comments' : ''}`}
          onClick={() => setCommentsOpen((value) => !value)}
          aria-expanded={commentsOpen}
          aria-label="Toggle comments"
          title="Comments"
        >
          <FontAwesomeIcon icon={faComment} />
          <span className="news-comments-badge">{article.commentCount || 0}</span>
        </button>
      </div>

      {isAdmin ? (
        <div className="news-admin-actions">
          {article.adminPinned ? (
            <span className="news-badge news-badge-pinned">Admin Pinned</span>
          ) : null}
          <button type="button" disabled={adminBusy} onClick={handleAdminPin}>
            {article.adminPinned ? 'Remove Admin Pin' : 'Admin Pin'}
          </button>
          {article.isHidden ? (
            <button type="button" disabled={adminBusy} onClick={() => handleAdminHide(false)}>
              Restore
            </button>
          ) : (
            <button type="button" disabled={adminBusy} onClick={() => handleAdminHide(true)}>
              Remove
            </button>
          )}
        </div>
      ) : null}

      <div ref={commentsWrapRef}>
        <NewsComments
          articleId={article.id}
          articleCollection={articleCollection}
          isOpen={commentsOpen}
          currentUser={currentUser}
          onCommentAdded={() => setCommentsOpen(false)}
        />
      </div>
    </article>
  );
}
